import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as URI from 'urijs';
import 'urijs/src/URITemplate';
import { isEmpty, chunk } from 'lodash';

import { getFriendsReferralEndpoint } from '../../utils/ubi-services';

import {
    ACCEPT_INVITATION_URL,
    ERROR_TYPE,
    FRIEND_INVITEES_URL,
    GET_CAMPAIGN_INFO_URL,
    GET_CAMPAIGN_STATUS_URL,
    GET_INVITE_INFO_URL,
    GET_PLAYERS_URL,
    GET_REWARDS_INFO_URL,
    PLATFORM_TO_REFERRAL_PLATFORM_ID,
    PLAYER_ERRORS_CODES,
    REFERRAL_PLATFORM_ID_TO_PLATFORM,
    REGISTER_PLAYER_URL,
    GET_PLAYERS_ELIGIBILITY_URL,
    CHECK_REFERRER_ELIGIBILITY_URL,
    CONVERSION_RULES_ID_TO_TYPES,
    GET_INVITEE_CONVERSION_STATUS
} from './friends-referral-api.constants';

import {
    ICampaignStatus,
    ICampaignInfo,
    ICampaignInfoResponse,
    IConfigureFriendsReferralServiceApiOptions,
    IConfigureFriendsReferralServiceSessionOptions,
    IFriendsReferralAPIError,
    IFriendsReferralError,
    IFriendsReferralResponse,
    IFRInviteeData,
    IGetInviteInfo,
    IGetPlayerInfo,
    IGetRewardsResponseItem,
    IRewardsResponseList,
    IGetInviteesResponse,
    IRegisterReferrerResult,
    IPlayersEligibilityResponse,
    IPlatformsEligibility,
    IGetEligibilityResponse,
    IGetConversionStatus
} from './friends-referral-api.types';
import { PLATFORM_TYPES } from '../../constants/platforms';
import { isBeforeExpiration, checkRewardEligible } from './friends-referral-api.utils';

const FR_API_QUERY_LIMIT = 50;

export class FriendsReferralApiService {
    private static AXIOS_INSTANCE: AxiosInstance;
    private static ENV: string;
    private static CAMPAIGN_ID: string;
    private static USER_ID: string;

    private static API_CONFIGURED = false;
    private static SESSION_CONFIGURED = false;

    /** Provide headers and other options to each request */
    public static configureSession(options: IConfigureFriendsReferralServiceSessionOptions) {
        this.AXIOS_INSTANCE.defaults.headers = {
            'Authorization': `Bearer ${options.ticket}`
        };
        this.USER_ID = options.userId;

        this.SESSION_CONFIGURED = true;
    }

    public static configureAPI(options: IConfigureFriendsReferralServiceApiOptions) {
        this.AXIOS_INSTANCE = axios.create();
        this.ENV = options.env;
        this.CAMPAIGN_ID = options.campaignId;

        this.API_CONFIGURED = true;
    }

    public static configureFakeAPI(): Promise<void> {
        /* eslint-disable-next-line*/
        return import('./__mocks__/friend-referral-api.service.mocks').then((module: any): void => {
            this.getInvitees = module.getInvitees;
            this.inviteFriends = (): Promise<void> => Promise.resolve();
            this.getRewardsInfo = module.getRewardsInfo;
        });
    }

    public static request<T>(options: AxiosRequestConfig): Promise<T> {
        this.checkIfApiConfigured();

        return this.AXIOS_INSTANCE.request({ ...options, responseType: 'json' })
            .then((response: AxiosResponse<IFriendsReferralResponse>): T => {
                const error = !response.data.isSuccessful;
                if (error) {
                    throw response.data;
                }
                return response.data.success!;
            }).catch((error: AxiosError | IFriendsReferralResponse): never => {
                if ((error as AxiosError).response) {
                    throw this.getNetworkErrorFromResponse(error as AxiosError);
                } else {
                    throw this.getFriendsReferralErrorFromResponse((error as IFriendsReferralResponse).failure!);
                }
            });
    }

    public static register(platform: PLATFORM_TYPES): Promise<IRegisterReferrerResult> {
        const url: string = this.getRegisterURL(PLATFORM_TO_REFERRAL_PLATFORM_ID[platform]).href();

        return this.request<IRegisterReferrerResult>({ url, method: 'POST' });
    }

    public static getPrivateKey(key: string) {
        return this[key];
    }

    public static acceptInvitation(options: { invitationId: string }): Promise<IFriendsReferralResponse> {
        const url: string = this.getAcceptInvitationUrl().setQuery({ invitationId: options.invitationId }).href();
        return this.request<IFriendsReferralResponse>({ url, method: 'POST' }).then();
    }

    public static getEligibilityInfo(): Promise<IPlatformsEligibility> {
        const url: string = this.getReferrerEligibilityURL().href();

        return this.request<IGetEligibilityResponse>({ url, method: 'GET' }).then(eligibilityResponse => {
            return eligibilityResponse.platformEligibility.reduce(
                (result, item) => {
                    const platformType = REFERRAL_PLATFORM_ID_TO_PLATFORM[item.platformId];
                    result[platformType] = item.isEligible;
                    return result;
                },
                {});
        });
    }

    public static getPlayersEligibility(ids: string[]): Promise<IPlayersEligibilityResponse> {
        this.checkIfSessionConfigured();

        const url = this.getPlayersEligibilityUrl().href();

        return this.request<IPlayersEligibilityResponse>({
            url,
            method: 'POST',
            headers: {
                ...this.AXIOS_INSTANCE.defaults.headers,
                'Ubi-UserId': this.USER_ID
            },
            data: ids
        });
    }

    public static getPlayers(ids: string[]): Promise<IFRInviteeData[]> {
        this.checkIfSessionConfigured();

        const url = this.getPlayersUrl().href();

        return this.request<IFRInviteeData[]>({
            url,
            method: 'POST',
            headers: {
                ...this.AXIOS_INSTANCE.defaults.headers,
                'Ubi-UserId': this.USER_ID
            },
            data: ids
        });
    }

    public static getCampaignInfo(): Promise<ICampaignInfo> {
        const url: string = this.getCampaignInfoURL().href();
        return this.request<ICampaignInfoResponse>({ url, method: 'GET' })
            .then(({
                campaignSpaces,
                conversionConfig: { conjunction, rules },
                name,
                crossPlatform,
                softClosingDate,
                endDate,
                platformLock
            }: ICampaignInfoResponse) => {
                const spaces = campaignSpaces.reduce((acc, space) => {
                    acc[REFERRAL_PLATFORM_ID_TO_PLATFORM[space.platformId]] = space.spaceIdStats;
                    return acc;
                }, {});
                const availableRules = rules.filter(rule => CONVERSION_RULES_ID_TO_TYPES[rule.type]);

                return {
                    spaces,
                    conversion: { conjunction, rules: availableRules },
                    name,
                    crossPlatform,
                    endDate,
                    platformLock,
                    softCloseMode: !isBeforeExpiration(softClosingDate)
                } as ICampaignInfo;
            });
    }

    public static getPlayerInfo(userId: string): Promise<IGetPlayerInfo> {
        return this.getPlayers([userId])
            .then(playerInfos => {
                if (isEmpty(playerInfos)) {
                    return {
                        isReferrer: false // is always 'false' if API responds with 'PLAYER_IS_NOT_REGISTERED' error
                    };
                }

                const [{
                    invitationId,
                    referrerUbiAccountId,
                    referrerId,
                    platformId,
                    isConverted
                }] = playerInfos;

                return {
                    isReferrer: true, // is always 'true' if API responds with any info
                    invitationId,
                    referrerUbiAccountId,
                    referrerId,
                    isConverted,
                    platformId: platformId ? REFERRAL_PLATFORM_ID_TO_PLATFORM[platformId] : null
                };
            })
            .catch(error => {
                if (
                    ERROR_TYPE.API_ERROR === error.type
                    && PLAYER_ERRORS_CODES.PLAYER_IS_NOT_REGISTERED === error.code
                ) {
                    return {
                        isReferrer: false // is always 'false' if API responds with 'PLAYER_IS_NOT_REGISTERED' error
                    };
                }
                return error;
            });
    }

    public static getInvitees(options: { page: number, size?: number }): Promise<IGetInviteesResponse> {
        const url: string = this.getInviteesURL(options).href();
        return this.request<IGetInviteesResponse>({ url, method: 'GET' });
    }

    public static inviteFriends(ubiIdList: string[]): Promise<void> {
        const url: string = this.getInviteesURL({}).href();

        if (ubiIdList.length <= FR_API_QUERY_LIMIT) {
            return this.request({ url, method: 'POST', data: ubiIdList }).then((): void => undefined);
        }

        // Split into multiple calls if id list exceeds API query limit
        const chunkedIds = chunk(ubiIdList, FR_API_QUERY_LIMIT);
        const requests = chunkedIds.map(idsChunk => this.request({
            url,
            method: 'POST',
            data: idsChunk
        }));

        return Promise.all(requests).then((): void => undefined);
    }

    public static getRewardsInfo(): Promise<IRewardsResponseList> {
        const url: string = this.getRewardsInfoURL().href();
        return this.request<IGetRewardsResponseItem[]>({
            url,
            headers: {
                'Authorization': null
            },
            method: 'GET'
        }).then(rewardsResponse => {
            /* We group all rewards into tiers by 'levelNumber' which become an 'id' of tier */
            return rewardsResponse.reduce((result, rewardItem) => {
                const isEligibleReward = checkRewardEligible(rewardItem);

                if (!isEligibleReward) {
                    return result;
                }

                if (isEmpty(result[rewardItem.levelNumber])) {
                    result[rewardItem.levelNumber] = { rewardsIds: [] };
                } else {
                    if (result[rewardItem.levelNumber].levelLimit !== rewardItem.levelLimit) {
                        throw 'Misconfiguration: items with different "levelNumbers" can\'t be grouped';
                    }
                }
                result[rewardItem.levelNumber].levelLimit = rewardItem.levelLimit;
                result[rewardItem.levelNumber].rewardsIds.push(rewardItem.id);
                result[rewardItem.levelNumber].levelRepeat = rewardItem.levelRepeat;
                return result;
            }, {});
        });
    }

    public static async getCampaignStatus(): Promise<ICampaignStatus> {
        this.checkIfApiConfigured();
        const url = this.getCampaignStatusUrl().href();
        const options = {
            url,
            headers: {
                'Authorization': null
            },
            method: 'GET',
            responseType: 'json'
        };

        try {
            const response = await this.AXIOS_INSTANCE.request(options);
            return (response.data as ICampaignStatus);
        } catch (error) {
            throw this.getNetworkErrorFromResponse(error as AxiosError);
        }
    }

    public static getInviteInfo(options: { invitationId: string }): Promise<IGetInviteInfo> {
        const url: string = this.getInviteInfoUrl()
            .setQuery({ invitationId: options.invitationId }).href();
        return this.request<IGetInviteInfo>({ url, method: 'GET' })
            .then(invitationInfo => ({
                ...invitationInfo,
                platformId: REFERRAL_PLATFORM_ID_TO_PLATFORM[invitationInfo.platformId]
            }));
    }

    public static async getInviteeConversionStatus(options: {
        readonly campaignId: string,
        readonly userId: string
    }): Promise<IGetConversionStatus> {
        const host: string = getFriendsReferralEndpoint(this.ENV);
        const template: string = URI.expand!(GET_INVITEE_CONVERSION_STATUS, options).toString();
        const url: string = new URI(host)
            .segment(template)
            .href();
        return await this.request({ url, method: 'GET' });
    }

    private static getRegisterURL(platformId: number): URI {
        this.checkIfSessionConfigured();

        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID, userId: this.USER_ID, platformId };
        const template: string = URI.expand!(REGISTER_PLAYER_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getInviteesURL(options: { page?: number, size?: number }): URI {
        this.checkIfSessionConfigured();

        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID, userId: this.USER_ID };
        const template: string = URI.expand!(FRIEND_INVITEES_URL, values).toString();
        return new URI(host)
            .segment(template)
            .setQuery({ pageIndex: options.page, pageSize: options.size });
    }

    private static getReferrerEligibilityURL(): URI {
        this.checkIfSessionConfigured();

        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID, userId: this.USER_ID };
        const template: string = URI.expand!(CHECK_REFERRER_ELIGIBILITY_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getRewardsInfoURL(): URI {
        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID };
        const template: string = URI.expand!(GET_REWARDS_INFO_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getCampaignInfoURL(): URI {
        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID };
        const template: string = URI.expand!(GET_CAMPAIGN_INFO_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getInviteInfoUrl(): URI {
        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID, userId: this.USER_ID };
        const template: string = URI.expand!(GET_INVITE_INFO_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getAcceptInvitationUrl(): URI {
        const host: string = getFriendsReferralEndpoint(this.ENV);
        const values: object = { campaignId: this.CAMPAIGN_ID, userId: this.USER_ID };
        const template: string = URI.expand!(ACCEPT_INVITATION_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getPlayersEligibilityUrl(): URI {
        const host = getFriendsReferralEndpoint(this.ENV);
        const values = { campaignId: this.CAMPAIGN_ID };
        const template = URI.expand!(GET_PLAYERS_ELIGIBILITY_URL, values).toString();

        return new URI(host).segment(template);
    }

    private static getPlayersUrl(): URI {
        const host = getFriendsReferralEndpoint(this.ENV);
        const values = { campaignId: this.CAMPAIGN_ID };
        const template = URI.expand!(GET_PLAYERS_URL, values).toString();

        return new URI(host).segment(template);
    }

    private static getCampaignStatusUrl(): URI {
        const host = getFriendsReferralEndpoint(this.ENV);
        const values = { campaignId: this.CAMPAIGN_ID };
        const template = URI.expand!(GET_CAMPAIGN_STATUS_URL, values).toString();

        return new URI(host).segment(template);
    }

    /** Convert Friends Referral API error to application-specific error */
    private static getFriendsReferralErrorFromResponse(error: IFriendsReferralAPIError): IFriendsReferralError {
        return {
            code: parseInt(error.errorCode, 10),
            message: error.errorMessage,
            type: ERROR_TYPE.API_ERROR
        };
    }

    /** Convert network error to application-specific error */
    private static getNetworkErrorFromResponse(error: AxiosError): IFriendsReferralError {
        return {
            code: error.response!.status,
            message: error.message,
            type: ERROR_TYPE.NETWORK_ERROR
        };
    }

    private static checkIfApiConfigured() {
        if (!this.API_CONFIGURED) {
            throw new Error('API configuration is required, please check if config method is executed');
        }
    }

    private static checkIfSessionConfigured() {
        if (!this.SESSION_CONFIGURED) {
            throw new Error('Session configuration is required, please check if config methods is executed');
        }
    }
}
